/**
 * Logger Utility
 *
 * A robust, type-safe logger with support for different log levels, scoped logging,
 * and structured context. Designed to replace direct console.log usage throughout the application.
 */

/**
 * Log levels in order of increasing severity
 */
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Log entry structure
 */
interface LogEntry {
  /** ISO timestamp when the log was created */
  timestamp: string;
  /** Log level */
  level: LogLevel;
  /** Log message */
  message: string;
  /** Optional context object with additional data */
  context?: Record<string, unknown>;
  /** Application module/component where the log originated */
  module?: string;
  /** Optional request/operation ID for tracing */
  requestId?: string;
}

/**
 * Global sensitive data patterns to redact from logs
 */
const GLOBAL_SENSITIVE_KEYS = [
  // Authentication
  'password',
  'secret',
  'token',
  'api[_-]?key',
  'access[_-]?token',
  'refresh[_-]?token',
  'authorization',
  // Personal data
  'credit[_-]?card',
  'cc[_-]?number',
  'ssn',
  'social[_-]?security',
  'dob',
  'date[_-]?of[_-]?birth',
];

/**
 * Type for circular reference placeholders
 */
interface CircularReferencePlaceholder {
  __circularReference: true;
  __type: 'array' | 'object';
}

/**
 * Type for truncated object placeholders
 */
interface TruncatedObjectPlaceholder {
  __truncated: true;
  __type: 'array' | 'object';
  __depth: number;
}

/**
 * Type guard to check if an object is a circular reference placeholder
 */
export function isCircularReference(obj: unknown): obj is CircularReferencePlaceholder {
  return typeof obj === 'object' && obj !== null && '__circularReference' in obj;
}

/**
 * Type guard to check if an object is a truncated object placeholder
 */
export function isTruncatedObject(obj: unknown): obj is TruncatedObjectPlaceholder {
  return typeof obj === 'object' && obj !== null && '__truncated' in obj;
}

/**
 * Utility functions to check for special cases in redacted data:
 * - Use isCircularReference(obj) to check if an object is a circular reference placeholder
 * - Use isTruncatedObject(obj) to check if an object is a truncated object placeholder
 *
 * Example usage:
 * const redactedData = redactSensitiveData(someObject);
 * if (isCircularReference(redactedData)) {
 *   console.log('Found circular reference:', redactedData.__type);
 * }
 */

/**
 * Redacts sensitive data from objects while preserving structure
 * Handles circular references and deep nesting safely
 * Returns the original type T when possible, or special placeholders when necessary
 */
function redactSensitiveData<T extends Record<string, any>>(
  obj: T,
  sensitivePatterns: string[] = GLOBAL_SENSITIVE_KEYS,
  visited = new Set(),
  maxDepth: number = 10,
  currentDepth: number = 0,
): T {
  if (!obj || typeof obj !== 'object') return obj;
  if (visited.has(obj)) {
    // Return a properly typed placeholder for circular references
    return {
      __circularReference: true,
      __type: Array.isArray(obj) ? 'array' : 'object',
    } as unknown as T;
  }
  if (currentDepth >= maxDepth) {
    // Prevent stack overflow by truncating deeply nested objects
    return {
      __truncated: true,
      __type: Array.isArray(obj) ? 'array' : 'object',
      __depth: currentDepth,
    } as unknown as T;
  }

  visited.add(obj);

  const result: Record<string, any> = Array.isArray(obj) ? [] : {};

  for (const [key, value] of Object.entries(obj)) {
    const keyStr = String(key).toLowerCase();
    const isSensitive = sensitivePatterns.some((pattern: string) =>
      new RegExp(pattern, 'i').test(keyStr),
    );

    if (isSensitive) {
      result[key] = '[REDACTED]';
    } else if (value && typeof value === 'object' && !(value instanceof Error)) {
      result[key] = redactSensitiveData(
        value,
        sensitivePatterns,
        visited,
        maxDepth,
        currentDepth + 1,
      );
    } else {
      result[key] = value;
    }
  }

  return result as T;
}

/**
 * Log context with optional standard fields for better filtering and analysis
 */
interface LogContext extends Record<string, unknown> {
  /** User ID if available */
  userId?: string;
  /** Current route/path */
  path?: string;
  /** Additional error details for error logs */
  error?: unknown;
  /** Any other context-specific data */
  [key: string]: unknown;
}

/**
 * Logger configuration options
 */
interface LoggerOptions {
  /** Minimum log level to output */
  minLevel?: LogLevel;
  /** Whether to include timestamps in logs */
  includeTimestamp?: boolean;
  /** Whether to include the module name in logs */
  includeModule?: boolean;
  /** Custom formatter function */
  formatter?: (entry: LogEntry) => string;
  /** Whether to enable logging */
  enabled?: boolean;
  /** Whether to redact sensitive data from logs */
  redactSensitiveData?: boolean;
  /** Custom patterns to redact (in addition to default sensitive patterns) */
  additionalSensitivePatterns?: string[];
  /** Whether to include error stack traces in logs */
  includeErrorStack?: boolean;
  /** Maximum recursion depth for redacting sensitive data (default: 10) */
  maxRedactionDepth?: number;
}

/**
 * Singleton logger class for consistent logging throughout the application
 */
class Logger {
  private static _instance: Logger | undefined = undefined;
  private minLevel: LogLevel;
  private options: Required<LoggerOptions>;
  private readonly isDevelopment: boolean;
  private readonly logLevels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };
  private sensitivePatterns: string[];

  protected constructor(options: LoggerOptions = {}) {
    this.isDevelopment = process.env.NODE_ENV === 'development';

    // Set default values for all options
    this.options = {
      minLevel: this.isDevelopment ? 'debug' : 'info',
      includeTimestamp: true,
      includeModule: true,
      enabled: true,
      formatter: this.defaultFormatter.bind(this),
      redactSensitiveData: true,
      additionalSensitivePatterns: [],
      includeErrorStack: this.isDevelopment,
      maxRedactionDepth: 10,
      ...options,
    };

    this.minLevel = this.options.minLevel;

    // Merge global and instance-specific sensitive patterns
    this.sensitivePatterns = [
      ...GLOBAL_SENSITIVE_KEYS,
      ...(this.options.additionalSensitivePatterns || []),
    ];
  }

  /**
   * Get the singleton instance of the logger
   */
  public static getInstance(options?: LoggerOptions): Logger {
    if (Logger._instance === undefined) {
      Logger._instance = new Logger(options);
    } else if (options) {
      // Update options if provided
      Logger._instance.configure(options);
    }
    return Logger._instance;
  }

  /**
   * Configure the logger with new options
   */
  public configure(options: Partial<LoggerOptions>): void {
    this.options = { ...this.options, ...options };
    if (options.minLevel) {
      this.minLevel = options.minLevel;
    }

    // Update sensitive patterns if additionalSensitivePatterns changed
    if (options.additionalSensitivePatterns !== undefined) {
      this.sensitivePatterns = [
        ...GLOBAL_SENSITIVE_KEYS,
        ...(this.options.additionalSensitivePatterns || []),
      ];
    }
  }

  /**
   * Set the minimum log level
   */
  public setMinLevel(level: LogLevel): void {
    this.minLevel = level;
    this.options.minLevel = level;
  }

  /**
   * Check if a log level should be logged based on current configuration
   */
  protected shouldLog(level: LogLevel): boolean {
    if (!this.options.enabled) return false;
    return this.logLevels[level] >= this.logLevels[this.minLevel];
  }

  /**
   * Default log formatter
   */
  protected defaultFormatter(entry: LogEntry): string {
    const timestamp = entry.timestamp;
    const level = `[${entry.level.toUpperCase()}]`;
    const module = entry.module ? `[${entry.module}]` : '';
    const requestId = entry.requestId ? `[${entry.requestId}]` : '';
    return `${timestamp} ${level}${module}${requestId} ${entry.message}`;
  }

  /**
   * Internal log method that handles all log levels
   * @internal
   */
  public log(level: LogLevel, message: string, context: LogContext = {}, module?: string): void {
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const requestId = context?.requestId as string | undefined;

    // Process context (redact sensitive data if enabled)
    let processedContext = { ...context };

    if (this.options.redactSensitiveData) {
      processedContext = redactSensitiveData(
        processedContext,
        this.sensitivePatterns,
        new Set(),
        this.options.maxRedactionDepth,
      );
    }

    // Create log entry
    const logEntry: LogEntry = {
      timestamp,
      level,
      message,
      ...(Object.keys(processedContext).length > 0 && { context: processedContext }),
      ...(module && { module }),
      ...(requestId && { requestId }),
    };

    // In development, log to console with appropriate console method
    if (this.isDevelopment) {
      const logMethod =
        level === 'error' ? 'error' : level === 'warn' ? 'warn' : level === 'info' ? 'info' : 'log';

      // Format and log the message
      const formattedMessage = this.options.formatter(logEntry);

      if (Object.keys(context).length > 0) {
        console[logMethod](formattedMessage, context);
      } else {
        console[logMethod](formattedMessage);
      }
    }

    // In production, you might want to send logs to a logging service
    // Example: this.sendToLoggingService(logEntry);
  }

  /**
   * Create a scoped logger with a module name
   */
  public createScope(module: string): ScopedLogger {
    return new ScopedLogger(this, module);
  }

  // Public log methods

  public debug(message: string, context: LogContext = {}): void {
    this.log('debug', message, context);
  }

  public info(message: string, context: LogContext = {}): void {
    this.log('info', message, context);
  }

  public warn(message: string, context: LogContext = {}): void {
    this.log('warn', message, context);
  }

  public error(message: string, error?: Error | unknown, context: LogContext = {}): void {
    const errorContext: LogContext = {
      ...context,
      ...(error instanceof Error
        ? {
            error: {
              name: error.name,
              message: error.message,
              ...(this.isDevelopment &&
                this.options.includeErrorStack !== false && { stack: error.stack }),
            },
          }
        : { error }),
    };
    this.log('error', message, errorContext);
  }

  /**
   * Log performance metrics
   */
  public perf(operation: string, startTime: number, context: LogContext = {}): void {
    const duration = performance.now() - startTime;
    this.info(`${operation} completed in ${duration.toFixed(2)}ms`, {
      ...context,
      duration,
      operation,
    });
  }
}

/**
 * Scoped logger that includes a module name in all logs
 */
class ScopedLogger {
  constructor(
    private readonly parentLogger: Logger,
    private readonly moduleName: string,
  ) {}

  public debug(message: string, context: LogContext = {}): void {
    this.parentLogger.log('debug', message, context, this.moduleName);
  }

  public info(message: string, context: LogContext = {}): void {
    this.parentLogger.log('info', message, context, this.moduleName);
  }

  public warn(message: string, context: LogContext = {}): void {
    this.parentLogger.log('warn', message, context, this.moduleName);
  }

  public error(message: string, error?: Error | unknown, context: LogContext = {}): void {
    this.parentLogger.error(message, error, { ...context, module: this.moduleName });
  }

  public perf(operation: string, startTime: number, context: LogContext = {}): void {
    this.parentLogger.perf(operation, startTime, { ...context, module: this.moduleName });
  }

  public createScope(module: string): ScopedLogger {
    return new ScopedLogger(this.parentLogger, `${this.moduleName}:${module}`);
  }
}

// Export a singleton instance
export const logger = Logger.getInstance();

// Export the Logger class for advanced usage
export { Logger };

// Export types for external use (including ScopedLogger as type-only)
export type { LogLevel, LogEntry, LogContext, LoggerOptions, ScopedLogger };

// Example usage:
// import { logger } from './logger';
//
// // Basic logging
// logger.debug('Debug message');
// logger.info('Info message', { userId: '123', action: 'login' });
//
// // Error logging
// try {
//   throw new Error('Something went wrong');
// } catch (error) {
//   logger.error('An error occurred', error, { userId: '123' });
// }
//
// // Scoped logger
// const moduleLogger = logger.createScope('MyComponent');
// moduleLogger.info('Component initialized');
//
// // Performance timing
// const start = performance.now();
// // ... some operation
// logger.perf('Database query', start, { query: 'SELECT * FROM users' });
